import { useEffect, useState, useCallback } from 'react';
import { http } from '@/lib/api';
import { toast } from 'sonner';
import { ArrowLeft, MapPin, CheckCircle2, Filter, Loader2, Stethoscope } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import ContextualHelp from '@/components/ContextualHelp';
import { TherapistCard } from '@/components/TherapistCard';

const THERAPISTS_HELP = {
  whatIs: "The Therapist Directory connects you with culturally responsive, licensed mental health professionals who understand the lived experiences of men from every background.",
  whyMatters: "Finding a therapist who understands your lived experience is one of the strongest predictors of successful therapy. Every provider is vetted before appearing here.",
  howToUse: "Use the filters to narrow by state, specialty, how you want to be seen, insurance, or language, then reach out directly.",
  tips: [
    "The AI Coach is a solid thinking partner \u2014 not a replacement for therapy, but a bridge.",
    "If you're in crisis right now, the Support tab has hotlines and warm-line resources available 24/7.",
  ],
};

function Waitlist() {
  const [status, setStatus] = useState(null);
  const [state, setState] = useState('');
  const [specialty, setSpecialty] = useState('');
  const [submitting, setSubmitting] = useState(false);
  useEffect(() => { http.get('/therapists/waitlist/status').then((r) => setStatus(r.data)).catch(() => setStatus({ on_waitlist: false })); }, []);
  const join = async () => {
    setSubmitting(true);
    try { await http.post('/therapists/waitlist', { state, specialty }); toast.success('You\'re on the list.'); setStatus({ on_waitlist: true, record: { state, specialty } }); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Could not join waitlist'); } finally { setSubmitting(false); }
  };
  return (
    <div className="bg-beige rounded-3xl p-8 lg:p-10" data-testid="therapist-waitlist-card">
      {status?.on_waitlist ? (
        <div className="flex items-start gap-3">
          <CheckCircle2 size={22} className="text-green shrink-0 mt-0.5"/>
          <div>
            <p className="font-heading text-xl lg:text-2xl">You&apos;re on the list.</p>
            <p className="text-sm text-navy/70 mt-1">We&apos;ll email you the moment providers are available in your area.</p>
          </div>
        </div>
      ) : (
        <>
          <p className="font-heading text-xl lg:text-2xl">Notify me when available</p>
          <p className="text-sm text-navy/70 mt-1 mb-4">Tell us where you are and what you&apos;re looking for.</p>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 mb-4">
            <input data-testid="therapist-state" placeholder="State (e.g. GA)" value={state} onChange={(e) => setState(e.target.value)} className="bg-white border border-navy/10 rounded-2xl px-4 py-3 outline-none focus:ring-2 focus:ring-gold text-sm"/>
            <input data-testid="therapist-specialty" placeholder="Specialty (optional)" value={specialty} onChange={(e) => setSpecialty(e.target.value)} className="bg-white border border-navy/10 rounded-2xl px-4 py-3 outline-none focus:ring-2 focus:ring-gold text-sm"/>
          </div>
          <button onClick={join} disabled={submitting} data-testid="therapist-waitlist-btn" className="bg-navy text-white rounded-full px-6 py-3 hover:bg-navy-soft disabled:opacity-40">{submitting ? 'Adding…' : 'Notify me when available'}</button>
        </>
      )}
    </div>
  );
}

const LS_KEY = 'bh_therapist_filters_v1';
const EMPTY_FILTERS = { state: [], specialty: [], modality: [], insurance: [], language: [] };

export default function Therapists() {
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState(() => {
    try { const s = JSON.parse(localStorage.getItem(LS_KEY)); return (s && typeof s === 'object') ? { ...EMPTY_FILTERS, ...s } : { ...EMPTY_FILTERS }; }
    catch { return { ...EMPTY_FILTERS }; }
  });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const parts = [];
      Object.entries(filters).forEach(([k, arr]) => (arr || []).forEach((v) => parts.push(`${k}=${encodeURIComponent(v)}`)));
      const r = await http.get(`/therapists${parts.length ? '?' + parts.join('&') : ''}`);
      setData(r.data);
    } catch { toast.error('Could not load the directory'); }
    finally { setLoading(false); }
  }, [filters]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { try { localStorage.setItem(LS_KEY, JSON.stringify(filters)); } catch { /* ignore */ } }, [filters]);

  const facets = data?.facets || { states: [], specialties: [], insurance: [], languages: [], modalities: [] };
  const activeCount = Object.values(filters).reduce((a, arr) => a + (arr?.length || 0), 0);
  const hasDirectory = (data?.items?.length || 0) > 0 || activeCount > 0 || (facets.states.length + facets.specialties.length) > 0;

  const toggle = (dim, value) => setFilters((f) => {
    const cur = f[dim] || [];
    return { ...f, [dim]: cur.includes(value) ? cur.filter((x) => x !== value) : [...cur, value] };
  });
  const clearAll = () => setFilters({ ...EMPTY_FILTERS });
  const reset = () => { clearAll(); try { localStorage.removeItem(LS_KEY); } catch { /* ignore */ } };

  const DIMS = [
    { key: 'state', label: 'State', facet: 'states', fmt: (v) => v },
    { key: 'specialty', label: 'Specialty', facet: 'specialties', fmt: (v) => v },
    { key: 'modality', label: 'Format', facet: 'modalities', fmt: (v) => (v === 'telehealth' ? 'Telehealth' : v === 'in-person' ? 'In-person' : v) },
    { key: 'insurance', label: 'Insurance', facet: 'insurance', fmt: (v) => v },
    { key: 'language', label: 'Language', facet: 'languages', fmt: (v) => v },
  ];

  return (
    <div className="p-6 lg:px-10 lg:pt-10 space-y-6 pb-24" data-testid="therapists-page">
      <button onClick={() => nav(-1)} className="inline-flex items-center gap-2 text-sm text-navy/60 hover:text-navy"><ArrowLeft size={14}/> Back</button>

      <div className="bg-navy text-white rounded-3xl p-8 lg:p-12 card-soft">
        <p className="text-xs uppercase tracking-[0.2em] text-gold font-semibold">Find a Therapist</p>
        <h1 className="font-heading text-3xl lg:text-5xl mt-2 leading-tight flex items-start gap-2 flex-wrap">
          Culturally responsive care.<ContextualHelp pageTitle="Therapists" content={THERAPISTS_HELP} testid="therapists-help"/>
        </h1>
        <p className="text-white/75 text-base lg:text-lg mt-4 max-w-3xl">
          Licensed mental health professionals skilled in culturally responsive care for men of every background. Every provider is vetted before they appear here.
        </p>
        <button onClick={() => nav('/therapist-apply')} data-testid="therapist-apply-link"
          className="mt-5 inline-flex items-center gap-2 text-sm text-gold hover:text-white transition-smooth">
          <Stethoscope size={14}/> Are you a therapist? <span className="underline underline-offset-4">Apply to be listed →</span>
        </button>
      </div>

      {loading && <div className="text-center py-10 text-navy/45"><Loader2 className="inline animate-spin" size={20}/></div>}

      {!loading && !hasDirectory && (
        <>
          <p className="text-sm text-navy/60">Our vetted directory is launching soon. Join the waitlist and we&apos;ll notify you.</p>
          <Waitlist/>
        </>
      )}

      {!loading && hasDirectory && (
        <>
          {/* Multi-select faceted filters */}
          <div className="bg-white rounded-2xl p-4 card-soft" data-testid="therapist-filters">
            <div className="flex items-center gap-2 mb-3">
              <Filter size={14} className="text-navy/60"/>
              <span className="text-xs uppercase tracking-widest font-semibold text-navy/60">Filter</span>
              {activeCount > 0 && <span className="text-xs text-navy/45">· {activeCount} active</span>}
              <div className="ml-auto flex items-center gap-3">
                <button onClick={clearAll} data-testid="filter-clear-all" className="text-xs text-navy/60 hover:text-navy">Clear all</button>
                <button onClick={reset} data-testid="filter-reset" className="text-xs text-gold hover:underline">Reset</button>
              </div>
            </div>
            <div className="space-y-3">
              {DIMS.map(({ key, label, facet, fmt }) => {
                const opts = facets[facet] || [];
                if (!opts.length) return null;
                return (
                  <div key={key} data-testid={`filter-group-${key}`}>
                    <p className="text-[11px] uppercase tracking-widest text-navy/45 font-semibold mb-1.5">{label}</p>
                    <div className="flex flex-wrap gap-1.5">
                      {opts.map((o) => {
                        const on = (filters[key] || []).includes(o.value);
                        return (
                          <button key={o.value} onClick={() => toggle(key, o.value)} data-testid={`filter-${key}-${o.value}`}
                            className={`text-xs rounded-full px-3 py-1.5 transition-smooth ${on ? 'bg-navy text-white' : 'bg-[#F9F8F6] text-navy/70 hover:bg-beige'}`}>
                            {fmt(o.value)} <span className={on ? 'text-white/70' : 'text-navy/40'}>({o.count})</span>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <p className="text-sm text-navy/55">{data.total} therapist{data.total === 1 ? '' : 's'}</p>

          {data.items.length === 0 ? (
            <div className="bg-white rounded-2xl p-10 text-center card-soft" data-testid="therapist-no-match">
              <p className="font-heading text-lg">No matches with these filters.</p>
              <button onClick={clearAll} className="mt-3 text-sm text-gold hover:underline">Clear filters</button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
              {data.items.map((t) => <TherapistCard key={t.id} t={t}/>)}
            </div>
          )}
        </>
      )}

      <p className="text-xs text-navy/50 text-center pt-2">
        Brotherhood+ lists independent providers and is not a referral service or medical provider. Verify credentials with your state board before booking.
      </p>
    </div>
  );
}
